<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useConfig } from 'dashboard/composables/useConfig';

import SectionLayout from '../../account/components/SectionLayout.vue';
import AiProviderSection from '../components/AiProviderSection.vue';
import UsageLimitsSection from '../components/UsageLimitsSection.vue';
import ModelSelector from '../components/ModelSelector.vue';
import FeatureToggle from '../components/FeatureToggle.vue';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

defineProps({
  localAiSettings: { type: Object, required: true },
});

const emit = defineEmits(['featureToggle', 'modelChange']);

const { t } = useI18n();
const { captainEnabled } = useCaptain();
const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatwootCloud } = useAccount();
const captainConfigStore = useCaptainConfigStore();

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
    enterprise: true,
  },
  {
    key: 'copilot',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
    enterprise: true,
  },
]);

// General feature toggles (copilot/insights have their own tabs)
const featureToggles = computed(() => [
  { key: 'label_suggestion' },
  { key: 'help_center_search', enterprise: true },
  { key: 'audio_transcription', enterprise: true },
]);

const shouldShowFeature = feature => {
  if (isOnChatwootCloud.value && captainEnabled) return true;
  if (feature.enterprise) return isEnterprise;
  return true;
};

const isFeatureAccessible = feature => {
  if (isOnChatwootCloud.value && captainEnabled) return true;
  if (feature.enterprise) {
    return isEnterprise && enterprisePlanName === 'enterprise';
  }
  return true;
};

const handleFeatureToggle = ({ feature, enabled }) => {
  emit('featureToggle', { feature, enabled });
};

const handleModelChange = ({ feature, model }) => {
  emit('modelChange', { feature, model });
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- AI Provider -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.AI_PROVIDER.TITLE')"
      :description="t('CAPTAIN_SETTINGS.AI_PROVIDER.DESCRIPTION')"
    >
      <AiProviderSection
        v-model:provider="localAiSettings.provider"
        v-model:api-key="localAiSettings.apiKey"
        v-model:model="localAiSettings.model"
        :api-key-configured="
          captainConfigStore.aiSettings.api_key_configured || false
        "
        :masked-api-key="captainConfigStore.aiSettings.masked_api_key || ''"
        :using-own-key="captainConfigStore.aiSettings.using_own_key || false"
      />
    </SectionLayout>

    <!-- Usage Limits -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE_LIMITS.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE_LIMITS.DESCRIPTION')"
      with-border
    >
      <UsageLimitsSection
        v-model:monthly-token-limit="localAiSettings.monthlyTokenLimit"
        :tokens-used="
          captainConfigStore.aiSettings.tokens_used_this_month || 0
        "
      />
    </SectionLayout>

    <!-- Model Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
      :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
      with-border
    >
      <div class="grid gap-4">
        <ModelSelector
          v-for="feature in modelFeatures"
          v-show="shouldShowFeature(feature)"
          :key="feature.key"
          :is-allowed="isFeatureAccessible(feature)"
          :feature-key="feature.key"
          :title="feature.title"
          :description="feature.description"
          @change="handleModelChange"
        />
      </div>
    </SectionLayout>

    <!-- Feature Toggles -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
      :description="t('CAPTAIN_SETTINGS.FEATURES.DESCRIPTION')"
      with-border
    >
      <div class="grid gap-4">
        <FeatureToggle
          v-for="feature in featureToggles"
          v-show="shouldShowFeature(feature)"
          :key="feature.key"
          :is-allowed="isFeatureAccessible(feature)"
          :feature-key="feature.key"
          @change="handleFeatureToggle"
          @model-change="handleModelChange"
        />
      </div>
    </SectionLayout>
  </div>
</template>
